import * as THREE from 'three';
import { HealthBar } from '../../HealthBar.js';
import { colored } from '../../Style.js';
import { NAVY, paint, bake, beam } from './units-style.js';

// Seaplane (helicopter): a high-wing floatplane with team-coloured wings and
// fuselage, twin floats and a spinning nose propeller. The Airship (super
// helicopter) is a big team-coloured gasbag with a timber gondola, engine pods
// and gold trim, drifting gently as it cruises.
export class Airplane {
  constructor(scene, unit, color, isSuper = false) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.isSuper = isSuper;
    this.mesh = null;
    this.geometries = [];
    this.props = [];
    this.lastPosition = null;
    this.targetRotation = 0;
    this.currentBank = 0;
    this.currentPitch = 0;
    this.phase = Math.random() * Math.PI * 2; // So formations don't bob in unison
    this.lastAnimTime = null;
    this.lastMeshPosition = new THREE.Vector3();
    this.healthBar = null;
    this.maxHealth = isSuper ? 90 : 30; // SuperHelicopterHealth vs AirplaneHealth
    this.create();
  }

  create() {
    // Scale factor for super helicopters (1.3x larger)
    const scale = this.isSuper ? 1.3 : 1.0;

    // Create parent group for positioning and yaw rotation
    this.mesh = new THREE.Group();

    // Create a yaw group (rotates around Y axis for heading)
    this.yawGroup = new THREE.Group();
    this.mesh.add(this.yawGroup);

    // Create a pitch/bank group for tilting; the craft faces +Z
    this.bankGroup = new THREE.Group();
    this.bankGroup.scale.setScalar(scale);
    this.yawGroup.add(this.bankGroup);

    const body = this.isSuper ? this.buildAirship() : this.buildSeaplane();
    this.bodyMesh = new THREE.Mesh(this.track(bake(body.parts)), paint());
    this.bodyMesh.castShadow = true;
    this.bodyMesh.receiveShadow = true;
    this.bankGroup.add(this.bodyMesh);

    // Propellers spin about their own Z axis
    const propGeometry = this.track(bake(this.buildProp(body.propRadius)));
    for (const [x, y, z] of body.props) {
      const prop = new THREE.Mesh(propGeometry, paint());
      prop.position.set(x, y, z);
      prop.rotation.z = Math.random() * Math.PI;
      this.bankGroup.add(prop);
      this.props.push(prop);
    }

    // Create health bar above the craft (on parent group so it stays upright)
    this.healthBar = new HealthBar(this.maxHealth, 3 * scale, 0.35);
    this.healthBar.getGroup().position.y = (this.isSuper ? 2.4 : 2.2) * scale;
    this.mesh.add(this.healthBar.getGroup());

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.lastMeshPosition.copy(this.mesh.position);

    this.scene.add(this.mesh);
  }

  track(geometry) {
    this.geometries.push(geometry);
    return geometry;
  }

  buildSeaplane() {
    const team = this.color;
    const parts = [
      // Fuselage tapering to the tail, white belly, dark cockpit
      colored(new THREE.CylinderGeometry(0.34, 0.16, 2.6, 8), team, { z: -0.35, rx: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.36, 0.36, 0.45, 8), NAVY.white, { z: 1.05, rx: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.22, 0.36, 0.25, 8), NAVY.iron, { z: 1.38, rx: Math.PI / 2 }),
      colored(new THREE.BoxGeometry(0.42, 0.26, 0.6), NAVY.coat, { y: 0.32, z: 0.45 }),
      colored(new THREE.SphereGeometry(0.11, 6, 4), NAVY.skin, { y: 0.4, z: 0.35 }),
      colored(new THREE.BoxGeometry(0.26, 0.08, 0.22), NAVY.woodDark, { y: 0.48, z: 0.35 }),
      // High wing on a pylon: team top, white tips and struts
      colored(new THREE.BoxGeometry(4.4, 0.1, 0.85), team, { y: 0.62, z: 0.35 }),
      colored(new THREE.BoxGeometry(0.5, 0.11, 0.86), NAVY.white, { x: -2.0, y: 0.62, z: 0.35 }),
      colored(new THREE.BoxGeometry(0.5, 0.11, 0.86), NAVY.white, { x: 2.0, y: 0.62, z: 0.35 }),
      colored(new THREE.BoxGeometry(0.3, 0.3, 0.5), team, { y: 0.45, z: 0.35 }),
      // Tail: fin with a white stripe and tailplane
      colored(new THREE.BoxGeometry(0.07, 0.6, 0.55), team, { y: 0.35, z: -1.5, rx: -0.25 }),
      colored(new THREE.BoxGeometry(0.08, 0.14, 0.4), NAVY.white, { y: 0.45, z: -1.52, rx: -0.25 }),
      colored(new THREE.BoxGeometry(1.4, 0.06, 0.4), team, { y: 0.06, z: -1.5 })
    ];
    // Twin floats on struts
    for (const sx of [-1, 1]) {
      const x = sx * 0.7;
      parts.push(colored(new THREE.CylinderGeometry(0.15, 0.12, 2.0, 6), NAVY.white, { x, y: -0.72, z: 0.25, rx: Math.PI / 2 }));
      parts.push(colored(new THREE.ConeGeometry(0.15, 0.35, 6), NAVY.white, { x, y: -0.72, z: 1.42, rx: Math.PI / 2 }));
      parts.push(colored(new THREE.BoxGeometry(0.31, 0.04, 2.0), team, { x, y: -0.66, z: 0.25 }));
      parts.push(beam([x, -0.6, 0.75], [sx * 0.2, -0.15, 0.75], 0.05, NAVY.iron));
      parts.push(beam([x, -0.6, -0.2], [sx * 0.2, -0.15, -0.2], 0.05, NAVY.iron));
      parts.push(beam([sx * 1.3, 0.58, 0.35], [sx * 0.3, -0.1, 0.35], 0.05, NAVY.iron));
    }
    return { parts, props: [[0, 0, 1.55]], propRadius: 0.6 };
  }

  buildAirship() {
    const team = this.color;
    const parts = [
      // Gasbag with white panel bands and gold trim rings at nose and tail
      colored(new THREE.SphereGeometry(1, 10, 8), team, { y: 0.9, scale: [1.05, 1.05, 2.5] }),
      colored(new THREE.CylinderGeometry(1.07, 1.07, 0.2, 10), NAVY.white, { y: 0.9, z: 0.6, rx: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.95, 0.95, 0.2, 10), NAVY.white, { y: 0.9, z: -1.0, rx: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.35, 0.35, 0.12, 8), NAVY.gold, { y: 0.9, z: 2.38, rx: Math.PI / 2 }),
      // Gondola slung beneath, with windows and a gold rail
      colored(new THREE.BoxGeometry(0.6, 0.42, 1.5), NAVY.wood, { y: -0.35, z: 0.2 }),
      colored(new THREE.BoxGeometry(0.62, 0.12, 1.2), NAVY.coat, { y: -0.28, z: 0.25 }),
      colored(new THREE.BoxGeometry(0.66, 0.05, 1.56), NAVY.gold, { y: -0.12, z: 0.2 }),
      colored(new THREE.ConeGeometry(0.3, 0.4, 4), NAVY.wood, { y: -0.35, z: 1.1, rx: Math.PI / 2, ry: Math.PI / 4 })
    ];
    for (const z of [-0.4, 0.8]) {
      for (const sx of [-1, 1]) {
        parts.push(beam([sx * 0.28, -0.15, z], [sx * 0.6, 0.2, z], 0.04, NAVY.rope));
      }
    }
    // Cruciform tail fins: white with team-coloured tips
    for (let i = 0; i < 4; i++) {
      const fin = colored(new THREE.BoxGeometry(0.06, 0.85, 0.9), NAVY.white, { y: 1.45, z: -2.05 });
      fin.translate(0, -0.9, 0).rotateZ(i * Math.PI / 2).translate(0, 0.9, 0);
      parts.push(fin);
      const edge = colored(new THREE.BoxGeometry(0.07, 0.12, 0.92), team, { y: 1.85, z: -2.05 });
      edge.translate(0, -0.9, 0).rotateZ(i * Math.PI / 2).translate(0, 0.9, 0);
      parts.push(edge);
    }
    // Engine pods on outriggers either side of the gondola
    const props = [];
    for (const sx of [-1, 1]) {
      parts.push(beam([sx * 0.3, -0.3, -0.2], [sx * 1.15, -0.3, -0.2], 0.06, NAVY.iron));
      parts.push(colored(new THREE.CylinderGeometry(0.16, 0.12, 0.6, 6), NAVY.iron, { x: sx * 1.2, y: -0.3, z: -0.2, rx: Math.PI / 2 }));
      parts.push(colored(new THREE.CylinderGeometry(0.17, 0.17, 0.08, 6), NAVY.gold, { x: sx * 1.2, y: -0.3, z: -0.48, rx: Math.PI / 2 }));
      props.push([sx * 1.2, -0.3, -0.56]);
    }
    return { parts, props, propRadius: 0.4 };
  }

  // Two-bladed wooden propeller with a brass spinner, turning about Z
  buildProp(radius) {
    return [
      colored(new THREE.BoxGeometry(radius * 2, 0.12, 0.04), NAVY.woodDark, { rx: 0.25 }),
      colored(new THREE.ConeGeometry(0.09, 0.18, 6), NAVY.brass, { z: 0.08, rx: Math.PI / 2 })
    ];
  }

  // Spinning propellers, a gentle bob and a nose-down tilt into movement;
  // the airship also yaws lazily as it drifts
  animate() {
    const now = performance.now() / 1000;
    const dt = this.lastAnimTime === null ? 0 : Math.min(now - this.lastAnimTime, 0.1);
    this.lastAnimTime = now;
    if (dt <= 0) return;

    const pos = this.mesh.position;
    const moved = Math.min(Math.hypot(pos.x - this.lastMeshPosition.x, pos.z - this.lastMeshPosition.z), 1);
    this.lastMeshPosition.copy(pos);
    const speed = Math.min(1, moved / dt / 5);

    const targetPitch = speed * (this.isSuper ? 0.04 : 0.12);
    this.currentPitch += (targetPitch - this.currentPitch) * Math.min(1, dt * 3);
    const t = now + this.phase;
    this.bankGroup.rotation.x = this.currentPitch + (this.isSuper ? Math.sin(t * 0.5) * 0.03 : 0);
    this.bankGroup.rotation.y = this.isSuper ? Math.sin(t * 0.3) * 0.08 : 0;
    this.bankGroup.position.y = Math.sin(t * (this.isSuper ? 0.7 : 1.6)) * (this.isSuper ? 0.18 : 0.1);

    const spin = dt * (this.isSuper ? 14 : 30);
    this.props.forEach((prop, i) => { prop.rotation.z += i % 2 ? -spin : spin; });
  }

  updatePosition(position) {
    if (this.mesh && this.yawGroup && this.bankGroup) {
      // Calculate rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          // atan2(dx, dz) gives the angle from +Z, the direction the craft faces
          const newTargetRotation = Math.atan2(dx, dz);

          // Calculate turn rate for banking effect
          let turnRate = newTargetRotation - this.targetRotation;
          while (turnRate > Math.PI) turnRate -= Math.PI * 2;
          while (turnRate < -Math.PI) turnRate += Math.PI * 2;

          // Target bank angle based on turn rate (bank into turns); airships barely lean
          const limit = this.isSuper ? 0.12 : 0.5;
          const targetBank = Math.max(-limit, Math.min(limit, -turnRate * 2));
          this.currentBank += (targetBank - this.currentBank) * 0.1;

          this.targetRotation = newTargetRotation;
        } else {
          // Reduce bank when not turning
          this.currentBank *= 0.95;
        }
      }

      // Smoothly interpolate yaw rotation (apply to yaw group)
      let rotationDiff = this.targetRotation - this.yawGroup.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.yawGroup.rotation.y += rotationDiff * (this.isSuper ? 0.06 : 0.15);

      // Apply banking to bank group (roll around local Z axis)
      this.bankGroup.rotation.z = this.currentBank;

      this.animate();

      // Store last position
      this.lastPosition = { x: position.x, y: position.y, z: position.z };
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  remove() {
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
    // Materials are shared; only geometries are ours
    this.geometries.forEach(geometry => geometry.dispose());
    this.geometries = [];
  }
}
